
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	StyledPopup,
	ModalContent,
	EditFacetModalBtnWrap,
	ModalFacetWrap,
	FacetWrapper,
	ModalFacetInfoWrap,
	FacetTitle,
	FacetTitleButtons
} from './style';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import NonDefaultModeValues from './NonDefaultModeValues';
import DefaultModeValues from './DefaultModeValues';
import { useRefs } from 'contexts/RefContext';
import Konva from 'konva';
import { debounce, getDeratedValue } from '../../util';
import { getFacetDefaultPitchDegrees, getFacetDefaultRowSpacingInMeters,
	getFacetDefaultStartingHeightInFeet } from '../FacetInputs/util';
import { KONVA_FACET_GROUP, DESIGN_MODES, MIN_CUSTOM_SHADING, MAX_CUSTOM_SHADING, PREDEFINED_SHADING_VALUES,
	BASE_SHADING,
	FACET_HIGHLIGHT_COLOR} from '../../constants';
import { resetAzimuthEditState, setAzimuthOrPitchForNewlyCreatedNrelFacets, setDeletedRoofIndexes,
	setRoofIndexes, setSelectedRoofAzimuth, setSelectedRoofIndex, setUserModificationState, updateTransition } from 'store/slices/ToolSlice';
import { toggleManualToolDoneButtonState, toggleManualToolModalState, toggleManualToolResetButtonState } from 'store/slices/ManualToolModal';
import { NewlyCreatedFacet } from 'store/slices/ToolSlice/types';
import Button from 'components/Basic/Button';
import { EnergyState } from 'store/slices/EnergySlice/types';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { toggleRecalculateLayoutModalSliceState } from 'store/slices/RecalculateLayoutSlice';
import { toggleResetChangesLayoutModalSliceState } from 'store/slices/ResetChangesSlice';
import { hasShadingAffectingTransform } from 'components/tool/Editor/util';

import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import useTranslatePanelPositionsMutation from 'hooks/api/facet/useTranslateFacet';
import { getRoofSegmentIDFromGroupID, KonvaGroupNames, resetAllTransformations } from 'components/tool/utils';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { appToast } from '../Basic/CustomToast';
import { selectActiveAction, TOOL_ACTION } from 'store/selectors/toolSelector';
import { isModeDefault, isInHouseSimulationMode as checkIsInHouseSimulationMode } from '../../util';
import { FacetEditShadingStatus, latestFacetShadingStatus } from 'api/editDsm';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';

const highlightTimer = 5000;
const isValid = (sh: number) =>
	PREDEFINED_SHADING_VALUES.includes(sh) ||
				(sh >= BASE_SHADING + MIN_CUSTOM_SHADING &&
				sh <= BASE_SHADING + MAX_CUSTOM_SHADING);

const isEdited = (value?: number, saved?: number) => Number.isFinite(value) && value != saved;

// debounce() must be created once and reused across renders; recreating it every render
// (as a plain `const x = debounce(...)` in the component body) orphans any pending timer,
// letting a stale keystroke's value overwrite a newer one before it lands.
function useStableDebounce(fn: (...args: any[]) => void, timeout: number) { // eslint-disable-line @typescript-eslint/no-explicit-any
	const fnRef = useRef(fn);
	fnRef.current = fn;
	return useRef(debounce((...args: any[]) => fnRef.current(...args), timeout)).current; // eslint-disable-line @typescript-eslint/no-explicit-any
}

const SideModal = () => {
	const dispatch = useDispatch<AppDispatch>();
	const { newlyCreatedFacets, roofIndexes, selectedRoofIndex, selectedUnfinalizedKonvaFacetId, editModeEnabled, 
		anyInvalidAzimuth , deletedRoofIndexes }
		= useSelector((state: RootState) => state.toolNewPostions.data);
	const { open: manualToolModalOpen, showAllFacets, inputFieldsEdited } =
		useSelector((state: RootState) => state.manualToolModal.data);
	const { allRoofSegs, panel, mode, roofSegsFromWhichUserHasSelectedPanels, uuid, designType, org, groundMetersPerPixel }
		= useSelector((state: RootState) => state.roofData.data);
	const { selectedPanels: selectedPanelsOnUI = [], deratedBy } = useSelector(
		(state: RootState) => (state.energyData.data ?? {}) as EnergyState
	);
	const { open:recalculateModalOpen } = useSelector((state: RootState) => state.recalculateLayoutModal.data);
	const { defaultShading } = useSelector((state: RootState) => state.QueryParams.data);
	const allowedActions = useSelector((state: RootState) => state.roleState.data.allowedActions);
	const canEditFacetProps = allowedActions.includes('translate-panels-to-new-position');
	const { konvaRef, selectFacetRef } = useRefs();
	const prevSelectedGrp = useRef<string | null>(null);
	const timeOut = useRef<NodeJS.Timeout>();
	const prevGroupColors = useRef<Map<Konva.Shape, string | CanvasGradient>>(new Map());

	const [selectedFacetID, setSelectedFacetID] = useState<string | null>(null);
	const selectedCombinedFacetID = selectedUnfinalizedKonvaFacetId || selectedRoofIndex;
	const pitchCanBeAutoDetected = mode != DESIGN_MODES.NEARMAP_NREL_MANUAL;
	const isSolarDesign = designType == 'SOLAR';
	
	const hasTransformedFacet = !!roofIndexes.length;
	const hasCreatedFacet = !!Object.keys(newlyCreatedFacets).length;
	const hasDeletedFacet = !!deletedRoofIndexes.length;

	const createdDeletedOrTransformedFacets = hasTransformedFacet || hasCreatedFacet || hasDeletedFacet;
	const hideDoneButton = createdDeletedOrTransformedFacets || anyInvalidAzimuth;
	const isDefaultMode = isModeDefault(mode);
	const isInHouseSimulationMode = checkIsInHouseSimulationMode(mode);
	const currentActiveActionOnTool = useSelector(selectActiveAction);
	const notInSubMenu = currentActiveActionOnTool == null || currentActiveActionOnTool == TOOL_ACTION.EDIT_AZIMUTH_OR_POSITION;

	const clearHighlight = useCallback((node?: Konva.Shape | Konva.Group) => {
		prevGroupColors.current.forEach((stroke, shape) => {
			shape.stroke(stroke);
		});
		prevGroupColors.current.clear();
		const layer = node?.getLayer() ?? konvaRef?.current?.getLayers()[0];
		layer?.batchDraw();
	}, [konvaRef]);

	useEffect(() => {
		if (editModeEnabled) {
			clearTimeout(timeOut.current);
			clearHighlight();
			prevSelectedGrp.current = null;
		}
	}, [editModeEnabled, clearHighlight]);

	const highlightGroup = useCallback((id: string, groupSuffix: string) => {
		setSelectedFacetID(id);
		if (!konvaRef?.current) return;
		clearTimeout(timeOut.current);

		// editMode ON + notInSubMenu: delegate directly to FacetGroup's selection logic
		if (editModeEnabled && notInSubMenu) {
			clearHighlight();
			prevSelectedGrp.current = id;
			selectFacetRef.current?.(id);
			return;
		}

		dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
		const isSameGroup = prevSelectedGrp.current === id;

		if (!isSameGroup) {
			if (editModeEnabled) {
				const prevFacet = konvaRef?.current?.findOne('#' + prevSelectedGrp.current) as Konva.Shape;
				clearHighlight(prevFacet);
			} else {
				const prevGroup = konvaRef?.current?.findOne('#' + prevSelectedGrp.current + groupSuffix) as Konva.Group;
				clearHighlight(prevGroup);
			}

			prevSelectedGrp.current = id;

			const group = konvaRef?.current?.findOne('#' + id + groupSuffix) as Konva.Group;
			if (!group) return;
			prevGroupColors.current.clear();
			(group.find('Shape') as Konva.Shape[]).forEach((shape) => {
				if (shape.getClassName() === 'Text') return;
				if (shape.findAncestors('.azimuth_selector').length > 0) return;
				prevGroupColors.current.set(shape, shape.stroke());
				shape.stroke('#0000ff');
			});
			group.getLayer()?.batchDraw();
		}

		if (editModeEnabled) {
			const facet = konvaRef?.current?.findOne('#' + id) as Konva.Shape;
			timeOut.current = setTimeout(() => { clearHighlight(facet); prevSelectedGrp.current = null; }, highlightTimer);
		} else {
			const group = konvaRef?.current?.findOne('#' + id + groupSuffix) as Konva.Group;
			timeOut.current = setTimeout(() => { clearHighlight(group); prevSelectedGrp.current = null; }, highlightTimer);
		}
	}, [dispatch, konvaRef, editModeEnabled, notInSubMenu, clearHighlight, selectFacetRef]);

	const debouncePitchDispatchForUserDefinedFacets = debounce((id: string, groupSuffix: string, value: number,) => {
		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
			facetId: id,
			pitchDegrees: value,
			isEdited: true,
		}));
	}, 200);

	const debounceRowSpacingDispatchForUserDefinedFacets = useStableDebounce((id: string, groupSuffix: string, value: number,) => {
		// NaN means the field was cleared after this write was scheduled - drop it instead of
		// persisting a stale keystroke's value once the timer finally fires
		if (Number.isNaN(value)) return;
		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
			facetId: id,
			rowSpacing: value,
			isEdited: true,
		}));
	}, 200);

	const debounceStartingHeightDispatchForUserDefinedFacets = useStableDebounce((id: string, groupSuffix: string, value: number,) => {
		if (Number.isNaN(value)) return;
		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
			facetId: id,
			startingHeight: value,
			isEdited: true,
		}));
	}, 200);

	const debounceShadingDispatchForUserDefinedFacets = debounce((id: string, value: number) => {
		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
			facetId: id,
			shading: value,
			isEdited: true,
		}));
		console.log('setting shading for group: ', id, 'value: ', value);
	}, 200);

	const debounceShadingForSystemDefinedFacets = debounce((id: string) => {
		if (!roofIndexes.includes(id)){
			dispatch(setRoofIndexes({ roofIndexes: roofIndexes.concat(id) }));
		}
		else{
			dispatch(setRoofIndexes({ roofIndexes: [...roofIndexes] }));
		}
		console.log('setting shading for group: ', id);
	}, 200);

	const debouncePitchForSystemDefinedFacets = debounce((id: string, groupSuffix: string, value: number) => {
		// TODO: handle the roof edit pitch properly
		if(!isSolarDesign){
			groupSuffix = `@${groupSuffix.split('@')[1]}`;
		}
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + id + groupSuffix);
		group?.setAttr('pitch', value);
		if (!roofIndexes.includes(id))
			dispatch(setRoofIndexes({ roofIndexes: roofIndexes.concat(id) }));
		else
			dispatch(setRoofIndexes({ roofIndexes: [...roofIndexes] }));
	}, 200);

	const debounceRowSpacingForSystemDefinedFacets = useStableDebounce((id: string, groupSuffix: string, value: number) => {
		if (Number.isNaN(value)) return;
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + id + groupSuffix);
		group?.setAttr('rowSpacing', value);
		if (!roofIndexes.includes(id))
			dispatch(setRoofIndexes({ roofIndexes: roofIndexes.concat(id) }));
		else
			dispatch(setRoofIndexes({ roofIndexes: [...roofIndexes] }));
	}, 200);

	const debounceStartingHeightForSystemDefinedFacets = useStableDebounce((id: string, groupSuffix: string, value: number) => {
		if (Number.isNaN(value)) return;
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + id + groupSuffix);
		group?.setAttr('startingHeight', value);
		if (!roofIndexes.includes(id))
			dispatch(setRoofIndexes({ roofIndexes: roofIndexes.concat(id) }));
		else
			dispatch(setRoofIndexes({ roofIndexes: [...roofIndexes] }));
	}, 200);

	// const debounceDispatch = debounce(
	// 	(id: string, obj: { [K in keyof NewlyCreatedFacet]: { key: K; value: NewlyCreatedFacet[K] } }[keyof NewlyCreatedFacet]) => {
	// 		if(!obj?.key || !obj?.value) return;
	// 		dispatch(setAzimuthOrPitchForNewlyCreatedNrelFacets({
	// 			facetId: id,
	// 			[obj?.key]: obj?.value
	// 		}));
	// 	}, 100);

	const selectedRoofSegs = useMemo(() => {
		if (editModeEnabled) return allRoofSegs.filter(r => r.panelsCount > 0);
		return allRoofSegs.filter((slcRoofSeg) => roofSegsFromWhichUserHasSelectedPanels.includes(slcRoofSeg.id));
	}, [allRoofSegs, editModeEnabled, roofSegsFromWhichUserHasSelectedPanels]);

	const updateShading = useCallback((id: string, groupSuffix: string, value: number) => {
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + id + groupSuffix);
		const selectedFacetKey = getRoofSegmentIDFromGroupID(group?.attrs.id);
		const roofSegIdx = selectedRoofSegs.findIndex((facet) => facet.id == selectedFacetKey);
		const prevShading = selectedRoofSegs[roofSegIdx]?.shading;
		const doesSystemDefinedShadingValueChanged = prevShading != value;
		// setting shading on konva group so that we can have latest value on konva
		// and dont have to rely on redux state value which upates after debounced function
		group?.setAttr('shading', value);
		if(groupSuffix.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[1] == KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME){
			debounceShadingDispatchForUserDefinedFacets(id, value);
		}
		if(groupSuffix.split(KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR)[1] == KONVA_FACET_GROUP.EXISTING_GROUP_NAME 
				&& doesSystemDefinedShadingValueChanged){
			debounceShadingForSystemDefinedFacets(id, value);
		}
	}, [debounceShadingDispatchForUserDefinedFacets, debounceShadingForSystemDefinedFacets, konvaRef, selectedRoofSegs]);

	const translatePanelPostionsQuery = useTranslatePanelPositionsMutation();
	const createNewFacetsQuery = useCreateDeleteFacet();

	const startShadingRecalc = useStartShadingRecalc();
	// Keep the highest dsmVersion seen across the create/translate responses.
	const startBackgroundShadingRecalc = (facetEditResults: PromiseSettledResult<FacetEditShadingStatus | undefined>[]) =>
		startShadingRecalc(latestFacetShadingStatus(facetEditResults));

	const handleQueiresSuccess = async () => {
		// rowSpacing/startingHeight live on the konva group only as unsaved overrides; the mutations above
		// have persisted them, so clear them and let the refetched facet values be the single source of truth
		(konvaRef?.current?.find(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group[] | undefined)
			?.forEach((g) => {
				g.setAttr('rowSpacing', undefined);
				g.setAttr('startingHeight', undefined);
			});
		await dispatch(changePanel({ uuid, panelKey: panel.key, designType: designType }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));
		dispatch(setDeletedRoofIndexes({ deletedRoofIndexes: [] }));
		dispatch(setUserModificationState({ hasMadeChanges: true }));
	};

	const handleQueries = async () => {
		try {
			// snapshot before the mutations: a plain move doesn't owe a recompute, a pitch/azimuth change does
			const hasShadingAffectingFacetTransform = hasTransformedFacet && !!konvaRef?.current
				&& hasShadingAffectingTransform(konvaRef.current, roofIndexes, allRoofSegs);
			const refreshShadingAfterFacetEdit = isInHouseSimulationMode
				&& (hasCreatedFacet || hasShadingAffectingFacetTransform || hasDeletedFacet);

			const facetEditResults = await Promise.allSettled(
				[createNewFacetsQuery.mutateAsync(), translatePanelPostionsQuery.mutateAsync()]);
			await handleQueiresSuccess();
			dispatch(toggleManualToolModalState({ value: false }));
			dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
			dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
			if (refreshShadingAfterFacetEdit) startBackgroundShadingRecalc(facetEditResults);

		} catch (error) {
			console.error('Error while executing queries:', error);
		}
	};

	const getUserDefinedFacet = useCallback((newlyCreatedFacet: NewlyCreatedFacet) => {

		const isActive = !selectedFacetID ?
			selectedCombinedFacetID === newlyCreatedFacet.konvaPolygonId : selectedFacetID === newlyCreatedFacet.konvaPolygonId;

		const newlyCreatedFacetCpy = {...newlyCreatedFacet};

		newlyCreatedFacetCpy.pitchDegrees ??= getFacetDefaultPitchDegrees({
			isGroundMount: newlyCreatedFacetCpy.isGroundMount,
			mode,
			isSolarDesign: designType === 'SOLAR',
			defaultFlatPitchDeg: org.setting.defaultFlatPitchDeg,
		});

		if(newlyCreatedFacetCpy.isGroundMount ){
			if(isInHouseSimulationMode){
				newlyCreatedFacetCpy.startingHeight ??= getFacetDefaultStartingHeightInFeet();
			}
			// Row spacing for flat facets
			if(!newlyCreatedFacetCpy.noGapGroundMount && newlyCreatedFacetCpy.pitchDegrees != null){
				newlyCreatedFacetCpy.rowSpacing ??= getFacetDefaultRowSpacingInMeters(
					newlyCreatedFacetCpy.pitchDegrees,
					groundMetersPerPixel
				);
			}
		}

		const isEditableGroundMount = newlyCreatedFacet?.isGroundMount && !isInHouseSimulationMode;
		const shouldAssignRealShading = isEditableGroundMount || !isDefaultMode;

		return {
			...newlyCreatedFacetCpy,
			shading: shouldAssignRealShading ? (newlyCreatedFacet?.shading ?? defaultShading) : undefined,
			active: isActive,
			pitchDegrees: (
				(pitchCanBeAutoDetected && !newlyCreatedFacet?.isGroundMount)
					? undefined
					: newlyCreatedFacetCpy.pitchDegrees
			),
			groupSuffix: KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KONVA_FACET_GROUP.IN_PROGRESS_GROUP_NAME,
			onChange: highlightGroup,
			onPitchValChange: debouncePitchDispatchForUserDefinedFacets,
			onShadingValChange: updateShading,
			onRowSpacingValChange: debounceRowSpacingDispatchForUserDefinedFacets,
			onStartingHeightValChange: debounceStartingHeightDispatchForUserDefinedFacets,
			canEditFacetProps: true,
			isEdited: true,
			enableShadingEdit: !isInHouseSimulationMode,
			enableStartingHeightEdit: isInHouseSimulationMode && isSolarDesign,
			enableRowSpacingEdit: isSolarDesign
		};

	}, [selectedFacetID, selectedCombinedFacetID, isInHouseSimulationMode, isDefaultMode, isSolarDesign, mode,
		defaultShading, pitchCanBeAutoDetected, highlightGroup,
		debouncePitchDispatchForUserDefinedFacets, updateShading,
		debounceRowSpacingDispatchForUserDefinedFacets,
		debounceStartingHeightDispatchForUserDefinedFacets, designType, org.setting.defaultFlatPitchDeg, groundMetersPerPixel]);

	const getSystemGerneratedFacetForNonDefaultMode = useCallback((facet: RasterRoofSegment) => {
		const groupSuffix = KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KonvaGroupNames.existingPanelsGroup;
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + facet.id + groupSuffix);
		const isActive = !selectedFacetID ? facet.id === selectedCombinedFacetID : facet.id === selectedFacetID;
		const { totalSelectedPanelsFromFacet, totalSelectedPanelsProduction } = (facet.panels ?? []).reduce((sum, panel) => ({
			totalSelectedPanelsFromFacet: sum.totalSelectedPanelsFromFacet + (selectedPanelsOnUI.includes(panel.id) ? 1 : 0),
			totalSelectedPanelsProduction: sum.totalSelectedPanelsProduction + (selectedPanelsOnUI.includes(panel.id) ? panel.yearlyEnergyDcKwh : 0)
		}), { totalSelectedPanelsFromFacet: 0, totalSelectedPanelsProduction: 0 });
		
		const { totalPanelsFromFacet, totalPanelsProduction } = (facet.panels ?? []).reduce((sum, panel) => ({
			totalPanelsFromFacet: sum.totalPanelsFromFacet + 1,
			totalPanelsProduction: sum.totalPanelsProduction + panel.yearlyEnergyDcKwh
		}), { totalPanelsFromFacet: 0, totalPanelsProduction: 0 });
		
		const resolvedPitchDegrees = group?.attrs.pitch ?? facet.pitchDegrees;
		const systemFacet: React.ComponentProps<typeof NonDefaultModeValues> = {
			hullCoords: [],
			konvaPolygonId: facet.id,
			azimuthDegrees: facet.azimuthDegrees,
			pitchDegrees: resolvedPitchDegrees,
			shading: group?.attrs.shading ?? facet.shading,
			previousPitch: facet.pitchDegrees,
			previousShading: facet.shading,
			panelsCount: totalSelectedPanelsFromFacet,
			systemSize: totalSelectedPanelsFromFacet * panel.wattage,
			systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
			active: isActive,
			groupSuffix,
			isGroundMount: !!facet?.isGroundMount,
			noGapGroundMount: !!facet?.noGapGroundMount,
			onChange: highlightGroup,
			onPitchValChange: debouncePitchForSystemDefinedFacets,
			onShadingValChange: updateShading,
			onRowSpacingValChange: debounceRowSpacingForSystemDefinedFacets,
			isEdited: roofIndexes.includes(facet.id),
			canEditFacetProps: canEditFacetProps,
			totalPanelsFromFacet: totalPanelsFromFacet,
			totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
			totalSystemSize: totalPanelsFromFacet * panel.wattage,
			showCompleteArrayDetails: editModeEnabled,
		};
		// Row spacing for flat facets
		if (facet.isGroundMount && !facet.noGapGroundMount) {
			systemFacet.rowSpacing = group?.attrs.rowSpacing
				?? getFacetDefaultRowSpacingInMeters(facet.pitchDegrees, groundMetersPerPixel);
		}

		return systemFacet;
	}, [canEditFacetProps, debouncePitchForSystemDefinedFacets,
		deratedBy, editModeEnabled, highlightGroup, konvaRef,
		panel.wattage, roofIndexes, selectedCombinedFacetID,
		selectedFacetID, selectedPanelsOnUI,
		updateShading, debounceRowSpacingForSystemDefinedFacets,
		groundMetersPerPixel
	]);

	const combinedObjsForNonDefaultMode = useMemo(() => {
		return [
			...Object.keys(newlyCreatedFacets).map(k => {
				const obj: React.ComponentProps<typeof NonDefaultModeValues> = getUserDefinedFacet(newlyCreatedFacets[k]);
				return obj;
			}),
			...selectedRoofSegs.map(o => getSystemGerneratedFacetForNonDefaultMode(o))
		];
	}, [newlyCreatedFacets, selectedRoofSegs, getUserDefinedFacet, getSystemGerneratedFacetForNonDefaultMode]);

	const selectedFacetDetailsForNonDefaultMode = useMemo(() => {
		if (selectedUnfinalizedKonvaFacetId) {
			const selectedUnfinalizedFacet = newlyCreatedFacets[selectedUnfinalizedKonvaFacetId];
			if (selectedUnfinalizedFacet) return getUserDefinedFacet(selectedUnfinalizedFacet);
		}

		const activeId = selectedRoofIndex ?? selectedFacetID;
		if (activeId) {
			const selectedFacet = allRoofSegs.find((s) => s.id === activeId);
			if (selectedFacet) return getSystemGerneratedFacetForNonDefaultMode(selectedFacet);
		}

		return null;
	}, [
		selectedRoofIndex,
		selectedFacetID,
		selectedUnfinalizedKonvaFacetId,
		allRoofSegs,
		getSystemGerneratedFacetForNonDefaultMode,
		newlyCreatedFacets,
		getUserDefinedFacet
	]);

	const getSystemGerneratedFacetForDefaultMode = useCallback((facet: RasterRoofSegment): React.ComponentProps<typeof DefaultModeValues> => {
		const groupSuffix = KONVA_FACET_GROUP.GROUP_NAME_SEPARATOR + KonvaGroupNames.existingPanelsGroup;
		const group: Konva.Group | undefined = konvaRef?.current?.findOne('#' + facet.id + groupSuffix);
		const isActive = !selectedFacetID ? facet.id === selectedCombinedFacetID : facet.id === selectedFacetID;
		const { totalSelectedPanelsFromFacet, totalSelectedPanelsProduction } = (facet.panels ?? []).reduce((sum, panel) => ({
			totalSelectedPanelsFromFacet: sum.totalSelectedPanelsFromFacet + (selectedPanelsOnUI.includes(panel.id) ? 1 : 0),
			totalSelectedPanelsProduction: sum.totalSelectedPanelsProduction + (selectedPanelsOnUI.includes(panel.id) ? panel.yearlyEnergyDcKwh : 0)
		}), { totalSelectedPanelsFromFacet: 0, totalSelectedPanelsProduction: 0 });

		const { totalPanelsFromFacet, totalPanelsProduction } = (facet.panels ?? []).reduce((sum, panel) => ({
			totalPanelsFromFacet: sum.totalPanelsFromFacet + 1,
			totalPanelsProduction: sum.totalPanelsProduction + panel.yearlyEnergyDcKwh
		}), { totalPanelsFromFacet: 0, totalPanelsProduction: 0 });

		const resolvedPitchDegrees = group?.attrs.pitch ?? facet.pitchDegrees;
		const systemFacet: React.ComponentProps<typeof DefaultModeValues> = {
			konvaPolygonId: facet.id,
			azimuthDegrees: facet.azimuthDegrees,
			pitchDegrees: resolvedPitchDegrees,
			active: isActive,
			groupSuffix,
			onChange: highlightGroup,
			systemProduction: getDeratedValue(totalSelectedPanelsProduction, deratedBy),
			panelsCount: totalSelectedPanelsFromFacet,
			systemSize: totalSelectedPanelsFromFacet * panel.wattage,
			previousPitch: facet.pitchDegrees,
			previousShading: facet.shading,
			isGroundMount: !!facet?.isGroundMount,
			noGapGroundMount: !!facet?.noGapGroundMount,
			enableShadingEdit: !isInHouseSimulationMode,
			shading: (facet.isGroundMount) ? group?.attrs.shading ?? facet.shading
				: facet.isShadingAdjusted ? (group?.attrs.shading ?? facet.shading) : undefined,
			onPitchValChange: debouncePitchForSystemDefinedFacets,
			area: facet.area,
			onShadingValChange: facet?.isGroundMount ? updateShading : undefined,
			onRowSpacingValChange: debounceRowSpacingForSystemDefinedFacets,
			onStartingHeightValChange: debounceStartingHeightForSystemDefinedFacets,
			isEdited: roofIndexes.includes(facet.id),
			canEditFacetProps: canEditFacetProps,
			totalPanelsFromFacet: totalPanelsFromFacet,
			totalPanelsProduction: getDeratedValue(totalPanelsProduction, deratedBy),
			totalSystemSize: totalPanelsFromFacet * panel.wattage,
			showCompleteArrayDetails: editModeEnabled,
			enableStartingHeightEdit: isInHouseSimulationMode && isSolarDesign,
			enableRowSpacingEdit: isSolarDesign,
		};

		if (facet.isGroundMount) {
			if (isInHouseSimulationMode) {
				systemFacet.startingHeight = group?.attrs.startingHeight ?? facet.startingHeight
					?? getFacetDefaultStartingHeightInFeet();
			}

			if (!facet.noGapGroundMount) {
				systemFacet.rowSpacing = group?.attrs.rowSpacing ?? facet.rowSpacing
					?? getFacetDefaultRowSpacingInMeters(facet.pitchDegrees, groundMetersPerPixel);
			}
		}

		return systemFacet;
	}, [konvaRef, selectedFacetID, selectedCombinedFacetID,
		highlightGroup, editModeEnabled, deratedBy,
		panel.wattage, debouncePitchForSystemDefinedFacets,
		updateShading, roofIndexes, canEditFacetProps,
		selectedPanelsOnUI, isInHouseSimulationMode, isSolarDesign,
		debounceRowSpacingForSystemDefinedFacets,
		groundMetersPerPixel,
		debounceStartingHeightForSystemDefinedFacets
	]);

	const combinedObjsForDefaultMode = useMemo(() => {
		return [
			...Object.keys(newlyCreatedFacets).map(k => {
				const obj: React.ComponentProps<typeof DefaultModeValues> = getUserDefinedFacet(newlyCreatedFacets[k]);
				return obj;
			}),
			...selectedRoofSegs.map(o => getSystemGerneratedFacetForDefaultMode(o))
		];
	}, [newlyCreatedFacets, getUserDefinedFacet, selectedRoofSegs, getSystemGerneratedFacetForDefaultMode]);

	const selectedFacetDetailsForDefaultMode = useMemo(() => {
		if(Object.keys(newlyCreatedFacets).length){
			const newAddedFacetsId = Object.keys(newlyCreatedFacets);
			if(newAddedFacetsId.length){
				return newAddedFacetsId.map((facetId) => getUserDefinedFacet(newlyCreatedFacets[facetId]));
			}
		}
		const activeId = selectedRoofIndex ?? selectedFacetID;
		const selectedFacet = allRoofSegs.find((s) => s.id === activeId);
		if (selectedFacet) return [getSystemGerneratedFacetForDefaultMode(selectedFacet)];

		return null;
	}, [allRoofSegs, getSystemGerneratedFacetForDefaultMode, getUserDefinedFacet, newlyCreatedFacets, selectedRoofIndex, selectedFacetID]);

	function FacetDetailsForNonDefaultMode() {
		const shouldShowAll = showAllFacets || (!selectedCombinedFacetID && !selectedFacetID && !Object.keys(newlyCreatedFacets).length);
		if (shouldShowAll) {
			return (
				<>
					{combinedObjsForNonDefaultMode.map((k) => (
						<NonDefaultModeValues
							key={k.konvaPolygonId}
							{...k}
						/>
					))}
				</>
			);
		}

		if (selectedFacetDetailsForNonDefaultMode) {
			return <NonDefaultModeValues {...{
				...selectedFacetDetailsForNonDefaultMode,
				active: true,
				enablePitchEdit: !isModeDefault(mode) ,
				enableShadingEdit: !isModeDefault(mode),
			}} />;
		}

		return null;
	}

	function FacetDetailsForDefaultMode() {
		const shouldShowAll = showAllFacets || (!selectedRoofIndex && !selectedFacetID && !Object.keys(newlyCreatedFacets).length);
		if (shouldShowAll) {
			return (
				<>
					{combinedObjsForDefaultMode.map((k) => (
						<DefaultModeValues
							key={k.konvaPolygonId}
							{...k}
						/>
					))}
				</>
			);
		}

		if (selectedFacetDetailsForDefaultMode) {
			if(Array.isArray(selectedFacetDetailsForDefaultMode)){
				return (
					<>
						{selectedFacetDetailsForDefaultMode.map((k) => (
							<DefaultModeValues
								key={k.konvaPolygonId}
								{...k}
							/>
						))}
					</>
				);
			}
		}

		return null;
	}

	const createNewFacetsQueryOrtranslatePanelPostionsQueryRunning =
		useQueriesRunningInBackGround(['create-new-facets', 'translate-panels'], 'mutating');

	const resetToLoadedState = debounce(async () => {
		const facetDetails: { [key: string]: { previousPitch: number, previousShading?: number, previousAzimuth: number } } = {};
		if(!isModeDefault(mode)){
			combinedObjsForNonDefaultMode.forEach((facet) => {
				if ('previousPitch' in facet && facet.previousPitch !== undefined &&
					'previousShading' in facet && facet.previousShading !== undefined &&
					'azimuthDegrees' in facet && facet.azimuthDegrees !== undefined) {
	
					facetDetails[facet.konvaPolygonId] = {
						previousPitch: facet.previousPitch,
						previousShading: facet.previousShading,
						previousAzimuth: facet.azimuthDegrees,
					};
				}
			});
		}
		else{
			combinedObjsForDefaultMode.forEach((facet) => {
				if ('previousPitch' in facet && facet.previousPitch !== undefined &&
					'azimuthDegrees' in facet && facet.azimuthDegrees !== undefined) {

					facetDetails[facet.konvaPolygonId] = {
						previousPitch: facet.previousPitch,
						previousShading: 'previousShading' in facet ? facet.previousShading as number | undefined : undefined,
						previousAzimuth: facet.azimuthDegrees,
					};
				}
			});
		}
		const groups = konvaRef?.current?.find(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group[];
		groups?.forEach((g) => {
			const facetKey = getRoofSegmentIDFromGroupID(g.attrs.id);
			const prevVals = facetDetails[facetKey];
			if (prevVals) {
				g.setAttr('pitch', prevVals.previousPitch);
				// no backend-persisted rowSpacing/startingHeight to revert to; clearing the overrides
				// lets them fall back to their computed defaults, recomputed against the pitch just reset above
				g.setAttr('rowSpacing', undefined);
				g.setAttr('startingHeight', undefined);
				if (prevVals.previousShading != null) {
					const rounded = +prevVals.previousShading.toFixed(2);
					g.setAttr('shading', isValid(rounded) ? rounded : undefined);
				} else {
					g.setAttr('shading', prevVals.previousShading);
				}
				// g.setAttr('azimuth', prevVals.previousAzimuth);
				// if(!(g.attrs.rotation == null)){
				// 	console.log(g);
				// }

				// // Set rotation
				// if (!(g.attrs.rotation == null)) {
				// 	g.rotation(prevVals.previousAzimuth);
				// 	g.position({ x: 0, y: 0 });
				// 	g.skew({ x: 0, y: 0 });
				// }
			}
		});
		if (konvaRef?.current) {
			resetAllTransformations(konvaRef.current);
		}
		dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
		dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
		dispatch(setRoofIndexes({ roofIndexes: [] }));
		dispatch(resetAzimuthEditState());
		dispatch(updateTransition({ currentTransitionState: false }));
		dispatch(setSelectedRoofIndex({ selectedRoofIndex: null }));
		dispatch(setSelectedRoofAzimuth({ selectedRoofAzimuth: null }));
	}, 250);

	const handleDoneBtnClick = debounce(async () =>  
	{
		let allValuesValid = false;
		if(konvaRef?.current){
			const existingFacetsShading: number[] = konvaRef?.current?.
				find(`.${KonvaGroupNames.existingPanelsGroup}`).
				map((g) => g.getAttr('shading')).
				filter((sh) => sh != undefined) ;
			
			const newlyCreatedFacetsShading: number[] = konvaRef?.current?.
				find(`.${KonvaGroupNames.inProgressUserDefinedFacetGroup}`).
				map((g) => g.getAttr('shading')).
				filter((sh) => sh != undefined) ;
			const allShading = [...existingFacetsShading ?? [], ...newlyCreatedFacetsShading ?? []];
			console.log('allShading: ', allShading);

			allValuesValid = allShading.every(isValid);

			if(!allValuesValid){
				const bad = allShading.filter((sh) => !isValid(sh));
				console.log('Invalid shading values:', bad);
			}
		}
		if(!allValuesValid){
			appToast.error('Please ensure all shading values are valid before proceeding.');
			return;
		}
		const editedFields = new Set<string>();
		const panelGroups = konvaRef?.current?.find(`.${KonvaGroupNames.existingPanelsGroup}`) as Konva.Group[];

		for (const group of panelGroups) {
			const roofSegmentId = getRoofSegmentIDFromGroupID(group.attrs.id);
			if (!roofIndexes.includes(roofSegmentId)) continue;

			const roofSegment = allRoofSegs.find((rs) => rs.id === roofSegmentId);
			if (isEdited(group.getAttr('pitch'), roofSegment?.pitchDegrees)) editedFields.add('roof pitch');
			if (isEdited(group.getAttr('rowSpacing'), roofSegment?.rowSpacing)) editedFields.add('row spacing');
			if (isEdited(group.getAttr('startingHeight'), roofSegment?.startingHeight)) editedFields.add('starting height');
		}
		const showResetLayoutMessage = editedFields.size > 0;

		if (showResetLayoutMessage) {
			const fields = new Intl.ListFormat('en').format(editedFields);
			dispatch(
				toggleRecalculateLayoutModalSliceState({
					open: true,
					warningMessage:
						`${fields[0].toUpperCase()}${fields.slice(1)} changes will recalculate the layout. Proceed?`,
				})
			);
			return;
		}
		dispatch(toggleResetChangesLayoutModalSliceState({
			open: false,
			warningMessage: '',
		}));
		dispatch(toggleRecalculateLayoutModalSliceState({
			open: false,
			warningMessage: '',
		}));
		dispatch(toggleManualToolModalState({ value: false }));
		dispatch(toggleManualToolDoneButtonState({ inputFieldsEdited: false }));
		dispatch(toggleManualToolResetButtonState({ resetEnabled: false }));
		dispatch(resetAzimuthEditState());
		await handleQueries();
	}, 250); 

	return (
		<StyledPopup
			open={manualToolModalOpen}
			closeOnDocumentClick={true}
			onClose={() => {
				setSelectedFacetID(null);
				if (inputFieldsEdited && !recalculateModalOpen) {
					dispatch(toggleResetChangesLayoutModalSliceState(
						{ 
							open: true, 
							warningMessage: 'Do you want to reset the changes and exit this menu?',
							combinedObjsForNonDefaultMode: combinedObjsForNonDefaultMode.map((f:any)=>({
								previousPitch: f?.previousPitch,
								previousShading: f?.previousShading,
								azimuthDegrees: f?.azimuthDegrees,
								konvaPolygonId: f?.konvaPolygonId,
							})),
							combinedObjsForDefaultMode: combinedObjsForDefaultMode.map((f:any)=>({
								previousPitch: f?.previousPitch,
								azimuthDegrees: f?.azimuthDegrees,
								konvaPolygonId: f?.konvaPolygonId,
							})),
						}
					));
					dispatch(toggleManualToolModalState({ value: false }));
				} else {
					dispatch(toggleManualToolModalState({ value: false }));
				}
			}}
			className={manualToolModalOpen ? 'open' : 'close'}
		>
			<div className="popup-modal-wrap">
				<ModalContent className="modal-content ">
					<ModalFacetWrap>
						<FacetWrapper>
							<FacetTitle>Facet Details:</FacetTitle>
							<FacetTitleButtons>
								{(inputFieldsEdited) && (
									<Button
										onClick={async () => {
											resetToLoadedState();
										}}
										className='btn-reset'>
										<svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
											<path d="M8.5 10.4853H11.1069M13.1123 8.94116V10.4853M13.1123 12.0294V10.4853M13.1123 
												10.4853H14.6764" stroke="var(--subHeadingColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											<path d="M14.6758 14.456H12.0688M10.0635 12.9119V14.456M10.0635 16.0001V14.456M10.0635 
												14.456H8.49933" stroke="var(--subHeadingColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
											<path d="M5.7317 13.5996C3.01246 12.8991 1 10.3877 1 7.39704C1 3.86405 3.80845 1 7.27284 1C10.2025 
											1 12.6631 3.0482 13.3529 5.81707M5.7317 13.5996V10.8863M5.7317 13.5996V13.7941H2.71077" 
											stroke="var(--subHeadingColor)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
											Reset
									</Button>
								)}
								{(inputFieldsEdited || hideDoneButton) && (
									<Button
										showLoader={createNewFacetsQueryOrtranslatePanelPostionsQueryRunning}
										disabled={createNewFacetsQueryOrtranslatePanelPostionsQueryRunning || anyInvalidAzimuth}
										onClick={handleDoneBtnClick}
									>
											Done
									</Button>
								)}
							</FacetTitleButtons>
						</FacetWrapper>
						<ModalFacetInfoWrap className="modal-facet-info-wrap">
							{
								isModeDefault(mode) ? 
									FacetDetailsForDefaultMode()
									: 
									FacetDetailsForNonDefaultMode()
							}
						</ModalFacetInfoWrap>
					</ModalFacetWrap>
				</ModalContent>
			</div>
		</StyledPopup>
	);
};

export default SideModal;

